import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { AnimatePresence, motion, useAnimate } from "motion/react";
import { useAuth, useMe } from "../auth.tsx";
import { api, useAction, useApi, type Resource } from "../api.ts";
import { fmtDate, fmtDateTime, fmtExpiry, daysLeft, plural } from "../format.ts";
import { Badge, Button, Card, EmptyState, Field, Icon, Input, LoadError, Loading, Mono, PageHeader, Table, TBody, Td, Th, THead, cn, useToast } from "../ui/index.tsx";
import { GameArt, PurchaseOptions } from "./_shared.tsx";
import { Burst, EASE, Springy, Stage, useCalm, useGameMap } from "./member/kit.tsx";
import type { Entitlement, Game, Redemption, RedeemResult } from "../../shared/types.ts";

const SCAN_STEPS = ["HANDSHAKE", "VERIFYING SIGNATURE", "BINDING LICENSE"];

function RedeemCard({ onRedeemed }: { onRedeemed: () => Promise<void> }) {
  const { refresh } = useAuth();
  const toast = useToast();
  const calm = useCalm();
  const [key, setKey] = useState("");
  const [step, setStep] = useState(0);
  const [burst, setBurst] = useState(0);
  const [shake, setShake] = useState(0);
  const [granted, setGranted] = useState<Entitlement[] | null>(null);

  const redeem = useAction(async () => {
    setGranted(null);
    const r = await api.post<RedeemResult>("/me/redeem", { key: key.trim() });
    setKey("");
    toast(r.entitlements.length ? `Redeemed. ${plural(r.entitlements.length, "game")} updated.` : "Key redeemed", "success");
    setGranted(r.entitlements);
    setBurst((b) => b + 1);
    await Promise.all([refresh(), onRedeemed()]);
    return r;
  });

  // Cycle the status line while the request is in flight (low-frequency, a few state updates in total).
  useEffect(() => {
    if (!redeem.pending) { setStep(0); return; }
    const t = setInterval(() => setStep((s) => Math.min(s + 1, SCAN_STEPS.length - 1)), 650);
    return () => clearInterval(t);
  }, [redeem.pending]);

  useEffect(() => {
    if (!granted) return;
    const t = setTimeout(() => setGranted(null), 6000);
    return () => clearTimeout(t);
  }, [granted]);

  const submit = async () => { if ((await redeem.run()) === undefined) setShake((s) => s + 1); };
  const onSubmit = (e: FormEvent) => { e.preventDefault(); void submit(); };

  return (
    <Card title="Redeem a license key" description="Keys look like EXO-XXXXX-XXXXX-XXXXX-XXXXX." hud className="relative overflow-visible">
      <form onSubmit={onSubmit} className="flex flex-col gap-3 sm:flex-row sm:items-start">
        <Field className="flex-1" error={redeem.error}>
          <motion.div key={shake} className="relative overflow-hidden rounded-lg" animate={shake ? { x: [0, -9, 9, -6, 6, 0] } : undefined} transition={{ duration: 0.4 }}>
            <Input value={key} onChange={(e) => setKey(e.target.value.toUpperCase())} placeholder="EXO-XXXXX-XXXXX-XXXXX-XXXXX" disabled={redeem.pending}
              className={cn("font-mono tracking-wide", redeem.pending && "border-accent/60 !opacity-100")} spellCheck={false} autoComplete="off" maxLength={40} />
            <AnimatePresence>
              {redeem.pending && (
                <motion.div className="pointer-events-none absolute inset-0" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} aria-hidden>
                  {!calm && (
                    <motion.div className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-accent/35 to-transparent"
                      initial={{ x: "-100%" }} animate={{ x: "300%" }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} />
                  )}
                  <div className="absolute inset-0 flex items-center justify-end px-3 font-mono text-[10px] tracking-[0.18em] text-accent">
                    <motion.span key={step} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>{SCAN_STEPS[step]}</motion.span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Field>
        <Springy><Button type="submit" variant="primary" icon="key" loading={redeem.pending} disabled={key.trim().length < 8}>Redeem</Button></Springy>
      </form>

      <Burst id={burst} />

      <AnimatePresence>
        {granted && (
          <motion.div className="mt-4 flex flex-wrap items-center gap-2 rounded-lg border border-ok/30 bg-ok/10 px-3 py-2.5 text-sm text-ok" role="status"
            initial={{ opacity: 0, height: 0, y: -6 }} animate={{ opacity: 1, height: "auto", y: 0 }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.35, ease: EASE }}>
            <motion.span initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 400, damping: 14 }}><Icon name="check" className="size-4" /></motion.span>
            <span className="font-mono text-[11px] uppercase tracking-[0.16em]">Access granted</span>
            {granted.map((e, i) => (
              <motion.span key={e.game_id} className="rounded-md border border-ok/30 bg-bg/40 px-2 py-0.5 text-xs text-fg"
                initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 400, damping: 18, delay: 0.15 + i * 0.07 }}>
                {e.game_name}
              </motion.span>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

function GameRow({ e, game, fresh }: { e: Entitlement; game: Game | undefined; fresh: boolean }) {
  const [scope, flash] = useAnimate<HTMLDivElement>();
  const prevExp = useRef(e.expires_at);
  const calm = useCalm();
  const lifetime = e.expires_at === null;
  const left = lifetime ? null : daysLeft(e.expires_at!);
  const soon = e.active && left !== null && left <= 7;

  useEffect(() => {
    const changed = prevExp.current !== e.expires_at;
    prevExp.current = e.expires_at;
    if ((changed || fresh) && !calm && scope.current) void flash(scope.current, { backgroundColor: ["rgba(255,61,79,0.28)", "rgba(255,61,79,0)"] }, { duration: 1.8, ease: "easeOut" });
  }, [e.expires_at]);

  return (
    <motion.div layout="position" initial={{ opacity: 0, x: -28 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 28, scale: 0.96 }}
      transition={{ type: "spring", stiffness: 340, damping: 30 }}>
      <div ref={scope} className="grid items-center gap-x-4 gap-y-2 px-5 py-3.5 transition-colors hover:bg-hover/50 sm:grid-cols-[minmax(0,1.5fr)_7rem_minmax(0,1.3fr)_8rem_6.5rem]">
        <div className="flex min-w-0 items-center gap-3">
          <GameArt game={{ name: e.game_name, image_url: game?.image_url ?? "", category: game?.category ?? "other" }} className="size-10 shrink-0 rounded-lg [&_span]:text-sm" />
          <span className="truncate font-medium">{e.game_name}</span>
        </div>
        <div><Badge tone={e.active ? "ok" : "bad"} dot>{e.active ? "Active" : "Expired"}</Badge></div>
        <div className="min-w-0 text-sm">
          <div className={cn(soon ? "text-warn" : "text-muted")}>{fmtExpiry(e.expires_at)}{e.expires_at && <span className="ml-2 text-xs text-faint">{fmtDate(e.expires_at)}</span>}</div>
          {e.active && !lifetime && (
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-raised">
              <motion.div className={cn("h-full rounded-full", soon ? "bg-warn" : "bg-ok")} initial={{ width: 0 }}
                animate={{ width: `${Math.min(100, Math.max(4, ((left ?? 0) / 30) * 100))}%` }} transition={{ duration: 1, ease: EASE }} />
            </div>
          )}
          {e.active && lifetime && <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-accent/40"><div className="h-full w-full bg-gradient-to-r from-accent via-ember to-accent" /></div>}
        </div>
        <div className="truncate text-sm text-muted" title={e.source}>{e.source}</div>
        <div className="whitespace-nowrap text-sm text-muted">{fmtDate(e.granted_at)}</div>
      </div>
    </motion.div>
  );
}

function MyGames({ list }: { list: Entitlement[] }) {
  const gameMap = useGameMap();
  // Rows present on first paint don't flash; anything that arrives later does.
  const initial = useRef(new Set(list.map((e) => e.game_id)));
  return (
    <Card title="Your games" padded={false} className="overflow-hidden">
      {list.length === 0 ? <EmptyState icon="gamepad" title="No games on your account yet" body="Redeem a key above or request a purchase below." /> : (
        <>
          <div className="hidden grid-cols-[minmax(0,1.5fr)_7rem_minmax(0,1.3fr)_8rem_6.5rem] gap-x-4 border-b border-line px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.14em] text-faint sm:grid">
            <span>Game</span><span>Status</span><span>Expiry</span><span>Source</span><span>Granted</span>
          </div>
          <div className="divide-y divide-line">
            <AnimatePresence initial={false} mode="popLayout">
              {list.map((e) => <GameRow key={e.game_id} e={e} game={gameMap.get(e.game_id)} fresh={!initial.current.has(e.game_id)} />)}
            </AnimatePresence>
          </div>
        </>
      )}
    </Card>
  );
}

function History({ redemptions }: { redemptions: Resource<Redemption[]> }) {
  return (
    <Card title="Redemption history" padded={false}>
      {redemptions.loading && !redemptions.data ? <Loading /> : redemptions.error && !redemptions.data ? <LoadError error={redemptions.error} retry={redemptions.reload} /> :
        (redemptions.data ?? []).length === 0 ? <EmptyState icon="key" title="No keys redeemed yet" /> : (
          <Table>
            <THead><tr><Th>Key</Th><Th>Games</Th><Th>Duration</Th><Th>Redeemed</Th></tr></THead>
            <TBody>
              {redemptions.data!.map((r, i) => (
                <motion.tr key={r.id} initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: Math.min(i, 8) * 0.04, ease: EASE }}
                  className="transition-colors hover:bg-hover/40">
                  <Td><Mono>{r.key_hint}</Mono></Td>
                  <Td>{r.games.join(", ") || "—"}</Td>
                  <Td className="text-muted">{r.days === null ? "Lifetime" : plural(r.days, "day")}</Td>
                  <Td className="whitespace-nowrap text-muted">{fmtDateTime(r.redeemed_at)}</Td>
                </motion.tr>
              ))}
            </TBody>
          </Table>
        )}
    </Card>
  );
}

/** After Stripe redirects back, the webhook may land a few seconds later, so poll /me briefly. */
function useCheckoutReturn() {
  const [params, setParams] = useSearchParams();
  const { refresh } = useAuth();
  const toast = useToast();
  useEffect(() => {
    if (params.get("checkout") !== "success") return;
    toast("Payment received. Your access will appear in a moment.", "success");
    setParams({}, { replace: true });
    const timers = [2000, 5000, 10000, 20000].map((ms) => setTimeout(() => void refresh(), ms));
    return () => timers.forEach(clearTimeout);
  }, []);
}

export default function Licenses() {
  const me = useMe();
  useCheckoutReturn();
  const redemptions = useApi<Redemption[]>("/me/redemptions");
  const sorted = [...me.entitlements].sort((a, b) => Number(b.active) - Number(a.active));
  const section = (i: number) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: 0.08 + i * 0.09, ease: EASE } });

  return (
    <Stage>
      <PageHeader title="Licenses" description="Redeem keys, see what you own, or request a purchase." />
      <div className="space-y-6">
        <motion.div {...section(0)}><RedeemCard onRedeemed={redemptions.reload} /></motion.div>
        <motion.div {...section(1)}><MyGames list={sorted} /></motion.div>
        <motion.div {...section(2)}><History redemptions={redemptions} /></motion.div>
        <motion.div {...section(3)}>
          <Card title="Buy a license" description="Pick a game and duration. We'll open a ticket to finish the purchase.">
            <PurchaseOptions />
          </Card>
        </motion.div>
      </div>
    </Stage>
  );
}
